import { useNavigate } from "react-router";
import { LogOut } from "lucide-react";
import {
  NAME_SAVED,
  PASSWORD_CHANGED,
  profileStateFor,
} from "@/modules/identity/commands/profileCopy";
import { DataUnavailable } from "@/design/DataUnavailable";
import { HowItWorksGuide } from "@/design/HowItWorksGuide";
import {
  ChangePasswordForm,
  DeleteAccountForm,
  ProfileNameForm,
} from "@/design/ProfileForms";
import { SettingsCard, SettingsSection } from "@/design/SettingsLayout";
import { SETTINGS_SECTIONS } from "@/design/settingsCopy";
import { Card } from "@/design/ui";
import { useMe } from "~/auth/MeProvider";
import { AppLink } from "~/components/AppLink";
import { ConsumerChrome } from "~/components/ConsumerChrome";
import { ScreenPending } from "~/components/ScreenPending";
import { forgetSession } from "~/auth/supabase";
import { post, put } from "~/lib/api";
import { useQuery } from "~/lib/useQuery";

/**
 * ConsumerProfileScreen (docs/ux/screens/ConsumerProfileScreen.md), the
 * Consumer's Settings: their account, and a guide to the product.
 *
 * **The phone is read-only** and is the verified identity (H22).
 *
 * **Deletion navigates rather than answering** (H34). The page it was called
 * from reads a profile that no longer exists, so staying here would render a
 * data-unavailable panel where an account used to be, which reads as a fault
 * rather than as the thing the person just asked for. The counts travel in the
 * URL because the account they describe is gone and there is nowhere else to
 * read them from; they identify nobody. The local session is discarded on the
 * way out, or the app would keep a token for an account that no longer exists.
 *
 * **It renders the navigation, which the Next page did not.** The screen doc
 * says this screen is "reached from the header while signed in" and that "the
 * header and page reflect the new name", and the server-rendered version shipped
 * without a header at all: from the Settings nav item there was no way back
 * except the browser button. Implementing the doc rather than the old page.
 *
 * **One page of sections, the guide among them** (2 November 2026). How it
 * works is a section like Profile or Security, above Session so the danger
 * zone stays last (H34). It reads no data, so it renders whatever the account
 * read is doing: a failed profile read is no reason to hide how the product
 * works.
 */
interface ProfilePayload {
  displayName: string;
  phone: string | null;
  phoneVerified: boolean;
}

export function ConsumerProfileScreen() {
  const navigate = useNavigate();
  const { reload, signOut } = useMe();
  const profile = useQuery<ProfilePayload>("/profile");

  // display_name is stored as one string; split on the first space to prefill
  // the first/last fields (a middle name folds into "last").
  const [firstName = "", ...rest] = (profile.data?.displayName ?? "").split(" ");
  const lastName = rest.join(" ");

  return (
    <main className="flex min-h-screen grow flex-col bg-page text-body">
      <ConsumerChrome />
      <div className="relative grow overflow-hidden px-4 py-10">
        <div className="mesh opacity-70" aria-hidden />
        <div className="relative mx-auto w-full max-w-3xl">
          <div className="enter mb-6">
            <h1 className="text-2xl font-extrabold text-ink sm:text-3xl">
              Settings
            </h1>
            <p className="mt-1 text-sm text-muted">
              Your account, and how Bookr works
            </p>
          </div>

          <div className="space-y-10">
            {profile.loading && !profile.data ? (
              <ScreenPending />
            ) : profile.failure || !profile.data ? (
              <Card>
                <DataUnavailable
                  configured={profile.failure !== "not_configured"}
                  what="your profile"
                />
              </Card>
            ) : (
              <>
                <SettingsSection id="profile" {...SETTINGS_SECTIONS.profile}>
                  <SettingsCard>
                    <ProfileNameForm
                      firstName={firstName}
                      lastName={lastName}
                      phone={profile.data.phone}
                      phoneVerified={profile.data.phoneVerified}
                      save={async (first, last) => {
                        const result = await put("/profile/name", {
                          firstName: first,
                          lastName: last,
                        });
                        if (!result.ok) return profileStateFor(result.failure);
                        // The chrome shows the display name, so it has to be
                        // re-read or the header keeps the old one until a reload.
                        await reload();
                        profile.reload();
                        return { success: NAME_SAVED };
                      }}
                    />
                  </SettingsCard>
                </SettingsSection>

                <SettingsSection id="security" {...SETTINGS_SECTIONS.security}>
                  <SettingsCard>
                    <ChangePasswordForm
                      save={async (currentPassword, newPassword, confirmPassword) => {
                        const result = await put("/profile/password", {
                          currentPassword,
                          newPassword,
                          confirmPassword,
                        });
                        return result.ok
                          ? { success: PASSWORD_CHANGED }
                          : profileStateFor(result.failure);
                      }}
                    />
                  </SettingsCard>
                </SettingsSection>
              </>
            )}

            <SettingsSection id="guide" {...SETTINGS_SECTIONS.guide}>
              <HowItWorksGuide Link={AppLink} />
            </SettingsSection>

            {profile.data && !profile.failure && (
              <>
                <SettingsSection id="session" {...SETTINGS_SECTIONS.session}>
                  <button
                    type="button"
                    className="group inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg border border-line bg-surface px-4 py-2.5 font-semibold text-ink shadow-sm transition hover:border-ink/30"
                    onClick={() => {
                      void signOut().then(() => navigate("/", { replace: true }));
                    }}
                  >
                    <LogOut
                      className="size-4 text-muted transition-transform duration-150 group-hover:translate-x-0.5"
                      aria-hidden
                    />
                    Log out
                  </button>
                </SettingsSection>

                {/* Last, and after log out. A destructive action that sits
                    among ordinary ones gets clicked by somebody aiming at the
                    row above it (H34). */}
                <SettingsSection id="danger" tone="danger" {...SETTINGS_SECTIONS.danger}>
                  <SettingsCard tone="danger">
                    <DeleteAccountForm
                      destroy={async (currentPassword) => {
                        const result = await post<{
                          deleted: {
                            bookingsCancelled: number;
                            bookingsAnonymised: number;
                          };
                        }>("/profile/delete", { currentPassword });
                        if (!result.ok) return profileStateFor(result.failure);

                        const { bookingsCancelled, bookingsAnonymised } =
                          result.value.deleted;
                        await forgetSession();
                        void navigate(
                          `/?deleted=1&cancelled=${bookingsCancelled}&kept=${bookingsAnonymised}`,
                          { replace: true },
                        );
                        return {};
                      }}
                    />
                  </SettingsCard>
                </SettingsSection>
              </>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
